import { Popover } from "@mui/material";
import Button from "components/ui/Button";
import classnames from "classnames";
import AppLanguageSwitcher, { LanguageDropdown } from "components/AppLanguageSwitcher";
import { useDictionaryTools } from "contexts/DictionaryToolsContext";
import { useLocalization } from "contexts/LocalizationContext";
import { UserContext, useUser } from "contexts/UserContext";
import CaretRightIcon from "images/caret_right.svg?react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import LoginIcon from "images/login_icon.svg?react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonalIcon from "images/personal_pic.svg?react";
import translateIcon from "images/translation_nav_icon.svg";
import { useRouteMeta } from "utils/routing";
import { MouseEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { isUserStudent, isUserTeacher, getUserFullName } from "services/users/utils";
import Logo from "components/Logo";
import UserNotifications from "./components/UserNotifications/UserNotifications";
import { useTabs } from "../../useTabs";
import NavPanel from "../NavPanel";
import "./PageHeader.css";
import { useRequiredContext } from "contexts/utils";

import { logError } from "services/logging";
function PageHeader() {
  const routeMeta = useRouteMeta();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const dictionaryTools = useDictionaryTools();
  const moreIconRef = useRef<null | HTMLButtonElement>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const location = window.location.pathname;
  const user = useUser(false);
  const { signOut } = useRequiredContext(UserContext);

  const handleSignOut = async () => {
    handleClose();
    await signOut();
  };
  const { language, changeLanguage } = useLocalization();

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const hdClickOption = (key: string) => {
    if (key !== "language") {
      handleClose();
      return;
    }

    if (moreIconRef.current) {
      setLanguageAnchorEl(moreIconRef.current);
    }
  };

  const { textTabs: textNavTabs, iconTabs: iconNavTabs } = useTabs();

  const shouldTakeTest =
    user?.student &&
    user.student.adaptive_test?.status !== "completed" &&
    user.student.access.adaptive_test;

  const name = getUserFullName(user?.profile);
  const isNotificationPanelVisible =
    user?.isTeacher || user?.isTeamManager || user?.isCompanyManager;

  const adaptiveTest = user?.student?.adaptive_test;
  const level =
    adaptiveTest?.status === "completed" ? adaptiveTest.evaluated_level?.name : null;

  return (
    <div className="page-header-container">
      {routeMeta.header && (
        <div className="page-header">
          <Logo />
          <div className="nav-panel-mobile-container">
            {textNavTabs.map(tab => {
              if (!tab.icon || !tab.path) {
                return null;
              }

              return (
                <NavLink
                  end
                  key={tab.name}
                  to={tab.path}
                  className={`nav-panel-mobile-icon ${location === tab.path ? "select" : ""}`}
                >
                  {tab.icon}
                </NavLink>
              );
            })}
            {dictionaryTools.enabled && (
              <Button
                variant="plain"
                className="nav-panel-mobile-icon"
                aria-label={t("Common.nav.tools")}
                onClick={dictionaryTools.toggleSidebar}
              >
                <img src={translateIcon} alt="" />
              </Button>
            )}
          </div>
          <div className="nav-panel-container">
            <NavPanel tabs={textNavTabs} linkClassName="webapp" />
          </div>
          <div className="icon-panel-container">
            <div className="icon-panel-mobile-divider" />
            {(isUserTeacher() || isUserStudent()) && (
              <AppLanguageSwitcher className={"language-btn"} />
            )}

            {isNotificationPanelVisible && <UserNotifications />}

            <div className="icon-panel-divider" />

            <button className="menu-button" onClick={handleOpenMenu}>
              <PersonalIcon />
              <div className="menu-button-container">
                <span className="menu-button-label">{name}</span>
                {shouldTakeTest && (
                  <span className="menu-button-text">
                    {t("Common.nav.takeLevelTest")}
                  </span>
                )}
                {!shouldTakeTest && level && (
                  <span className={"level-name"}>{level}</span>
                )}
              </div>
              <span
                className={classnames("menu-button-icon", {
                  opened: Boolean(anchorEl),
                })}
              >
                <ExpandMoreIcon />
              </span>
            </button>

            <button
              className="menu-mobile-button"
              onClick={handleOpenMenu}
              ref={moreIconRef}
            >
              <MoreHorizIcon />
            </button>

            <Popover
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{
                paper: {
                  className: "page-header-menu",
                },
              }}
            >
              {iconNavTabs.map(
                ({ icon, title, path, absolutePath, newTab, name: _name }, index) => {
                  if (path) {
                    return (
                      <Link
                        key={index}
                        to={path}
                        className="menu-item"
                        onClick={handleClose}
                      >
                        <span className="menu-icon">{icon}</span>
                        <span className="menu-label">{title}</span>
                      </Link>
                    );
                  }
                  if (absolutePath) {
                    return (
                      <a
                        key={index}
                        href={absolutePath}
                        className="menu-item"
                        onClick={handleClose}
                        {...(newTab && { target: "_blank" })}
                      >
                        <span className="menu-icon">{icon}</span>
                        <span className="menu-label">{title}</span>
                      </a>
                    );
                  }

                  if (_name === "language") {
                    return (
                      <button
                        key={index}
                        className="menu-item with-arrow only-mobile"
                        onClick={() => hdClickOption(_name)}
                      >
                        <div>
                          <span className="menu-icon">{icon}</span>
                          <span className="menu-label">{title}</span>
                        </div>

                        <CaretRightIcon />
                      </button>
                    );
                  }

                  return (
                    <button key={index} className="menu-item" onClick={handleClose}>
                      <span className="menu-icon">{icon}</span>
                      <span className="menu-label">{title}</span>
                    </button>
                  );
                }
              )}
              <button
                className="menu-item"
                onClick={() => {
                  handleSignOut().catch(logError);
                }}
              >
                <span className="menu-icon">
                  <LoginIcon />
                </span>
                <span className="menu-label">{t("Common.nav.signOut")}</span>
              </button>
            </Popover>
          </div>

          <LanguageDropdown
            anchorEl={languageAnchorEl}
            transformOrigin={{
              vertical: "top",
              horizontal: 235,
            }}
            onClose={() => setLanguageAnchorEl(null)}
            onSelect={changeLanguage}
            value={language}
          />
        </div>
      )}
    </div>
  );
}

export default PageHeader;
